// ============================================================================
// CONSULTING REPORT — MAIN COMPONENT
// Primary colour: Navy  #0747A6 / #0052CC / #043884
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { invokeWebAI } from '@advisor-core/lib/webAiClient';
import { AlertTriangle, CheckCircle2, Circle, ExternalLink, FileText } from 'lucide-react';
import {
  getModelMetrics, getModelMetricsTotal, getAppVersion,
  getIssueTypes, getProcessModelTitle, taskJSON,
  getModelID, getAiConsultantContent, getFieldUsage, getStatusTypeSummary, getStatusTypeMappings,
  getAttributePrivacyRules, getActivityType, getActivityContext, getConsultingWorkspace,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { applySampleItemPrivacy } from '@process-advisor/process-miner/consultant/attributePrivacyUtils';

import { openProgressModal }   from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal }  from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { startAiProgressTimer, stopAiProgressTimer, stopAllAiProgressTimers } from '@process-advisor/process-miner/consultant/aiProgressModalTimer';
import { apiProvider }         from '@process-advisor/process-miner/api/apiProvider';

import { buildConsultingReportPrompt } from '@process-advisor/process-miner/consultant/consultingReport-prompt';
import { buildSharedConsultantPayload } from '@process-advisor/process-miner/consultant/consultantSharedAiPayload';
import {
  renderConsultingReportHTML,
  buildConsultingReportExportHTML,
} from '@process-advisor/process-miner/consultant/consultingReport-renderer';
import { replaceQuotes, sleep } from '@process-advisor/process-miner/dashboard/aiinsights/aiInsights-utils';
import ExportModal from '@process-advisor/process-miner/consultant/ExportModal';
import { DEFAULT_REPORT_THEME } from '@process-advisor/process-miner/consultant/reportThemes';

// On-screen and default export theme follows the app edition:
// ServiceNow build ('SNOW') -> servicenow theme, Forge/Jira build -> atlassian.
const appDefaultReportTheme = () =>
  (getAppVersion() === 'SNOW' ? 'servicenow' : getAppVersion() ? 'atlassian' : DEFAULT_REPORT_THEME);
import { injectModalStyles }   from '@process-advisor/process-miner/dashboard/aiinsights/aiInsights-modal-styles';
import { saveConsultantContent } from '@process-advisor/process-miner/consultant/saveConsultantContent';
import { getCachedConsultantContent, setCachedConsultantContent, isHtmlCurrentVersion, getCachedConsultantJson, setCachedConsultantJson } from '@process-advisor/process-miner/consultant/consultantContentState';
import HtmlPreviewFrame from '@process-advisor/process-miner/consultant/HtmlPreviewFrame';
import ConsultantQaPanel, { AskAiButton } from '@process-advisor/process-miner/consultant/ConsultantQaPanel';
import ConsultantTabHeader, { ReadyBadge, getHeaderPrimaryButtonStyle, getHeaderSecondaryButtonStyle } from '@process-advisor/process-miner/consultant/ConsultantTabHeader';
import { SaveSnapshotButton, SaveSnapshotModal, SnapshotHeaderButton, SnapshotListPanel } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { saveSnapshot } from '@process-advisor/process-miner/consultant/consultantSnapshotService';
import { injectOrganizationContext, fetchOrganizationProfile, toOrganizationPromptContext } from '@process-advisor/process-miner/consultant/organizationProfile';
import useAiReportScopeControls from '@process-advisor/process-miner/consultant/useAiReportScopeControls';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

// ── Forge lazy loader ─────────────────────────────────────────────────────────
let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};


const DEFAULT_REPORT_OPTIONS = {
  includeExecutiveSummary: true,
  includeAssessmentScope: true,
  includeOrganizationContext: true,
  includeProcessEvidence: true,
  includeWorkbenchFindings: true,
  includeWorkbenchActions: true,
  includeAdvisorInsights: true,
  includeValueAnalysis: true,
  includeRoadmap: true,
  includeNextSteps: true,
  includeEvidenceAppendix: true,
};


const ADVISOR_CONTENT_SECTIONS = [
  { key: 'quickHealthcheck', label: 'Quick Healthcheck', column: 'ai_quick_healthcheck', tabId: 'quickHealthcheck' },
  { key: 'processHealth', label: 'Process Health Assessment', column: 'ai_process_health_assessment', tabId: 'processHealth' },
  { key: 'opportunities', label: 'AI, Automation & Optimisation', column: 'ai_intelligence', tabId: 'aiIntelligence' },
  { key: 'executionStrategy', label: 'Execution Strategy', column: 'ai_execution_strategy', tabId: 'aiExecutionStrategy' },
  { key: 'frameworkAssessment', label: 'Framework Assessment', column: 'ai_framework_assessment', tabId: 'frameworkAssessment' },
  { key: 'trendAnalysis', label: 'Trend Analysis', column: 'ai_trend_analysis', tabId: 'trendAnalysis' },
];

const REPORT_SECTION_DEFS = [
  { optionKey: 'includeExecutiveSummary', label: 'Executive summary', sourceKey: 'executiveSummary', required: true, description: 'Synthesised from available evidence, Advisor content, findings, actions, and value items.' },
  { optionKey: 'includeAssessmentScope', label: 'Assessment scope', sourceKey: 'scope', required: true, description: 'Model scope, source, fields, volume, date range, and assumptions.' },
  { optionKey: 'includeOrganizationContext', label: 'Organisation context', sourceKey: 'organization', description: 'Client/organisation details saved for tailoring the report.' },
  { optionKey: 'includeProcessEvidence', label: 'Workflow and evidence appendix', sourceKey: 'evidence', required: true, description: 'Workflow evidence, variants, transitions, bottlenecks, and supporting metrics.' },
  { optionKey: 'includeAdvisorInsights', label: 'Process Health and Advisor insights', sourceKey: 'advisor', required: true, description: 'Process Health anchor plus generated Advisor outputs such as healthcheck, AI/automation/optimisation opportunities, strategy, and framework assessment.' },
  { optionKey: 'includeWorkbenchFindings', label: 'Curated findings', sourceKey: 'findings', optional: true, description: 'Saved assessment findings selected by the consultant.' },
  { optionKey: 'includeWorkbenchActions', label: 'Curated actions', sourceKey: 'actions', optional: true, description: 'Saved improvement actions and implementation steps.' },
  { optionKey: 'includeValueAnalysis', label: 'Value analysis', sourceKey: 'value', description: 'Value findings/actions saved from the Value module.' },
  { optionKey: 'includeRoadmap', label: 'Improvement roadmap', sourceKey: 'roadmap', description: 'Generated from findings, actions, Advisor strategy, and selected next steps.' },
  { optionKey: 'includeNextSteps', label: 'Next steps', sourceKey: 'nextSteps', description: 'Client-ready follow-up actions based on available evidence.' },
  { optionKey: 'includeEvidenceAppendix', label: 'Evidence appendix', sourceKey: 'appendix', optional: true, description: 'Detailed supporting metrics, transitions, variants, and source evidence.' },
];

const GENERATION_STAGES = {
  checking: 'Checking assessment readiness',
  scope: 'Loading model scope and organisation context',
  snapshot: 'Preparing baseline metrics',
  map: 'Preparing process map data',
  evidence: 'Preparing metrics, variants and transitions',
  segments: 'Preparing segment and attribute evidence',
  advisor: 'Loading generated Advisor outputs',
  curated: 'Loading curated findings and actions',
  value: 'Loading value analysis and value-linked items',
  appendices: 'Building evidence appendices',
  package: 'Compiling comprehensive report package',
  writing: 'Writing Assessment Report narrative',
  rendering: 'Rendering comprehensive report preview',
  saving: 'Saving Assessment Report output',
};



// ── Normalise provider string for invoke ──────────────────────────────────────
const toInvokeProvider = (provider) =>
  provider === 'gemini' ? 'Gemini'
  : provider === 'claude' ? 'Claude'
  : 'ChatGPT';


const SLEEP_DELAY_MS = 50;
let processingCtr = 0;

// ── Robust JSON cleaner ───────────────────────────────────────────────────────
// Handles truncated/malformed AI responses: strips fences, trims to last valid
// closing brace, and attempts bracket-recovery on truncated arrays/objects.
const cleanAndParseJSON = (raw) => {
  let s = (raw || '{}')
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();

  // Trim to last closing brace (removes trailing commentary)
  const lastBrace = s.lastIndexOf('}');
  if (lastBrace !== -1) s = s.substring(0, lastBrace + 1);

  // First attempt — clean parse
  try { return JSON.parse(s); } catch (_) {}

  // Recovery: close unclosed brackets caused by token truncation
  const opens = [];
  for (const ch of s) {
    if (ch === '{' || ch === '[') opens.push(ch);
    else if (ch === '}' || ch === ']') opens.pop();
  }
  // Trim trailing incomplete element
  let recovered = s.replace(/,?\s*"[^"]*"?\s*:?\s*[^,\}\]]*$/, '');
  // Close all open brackets in reverse order
  for (let i = opens.length - 1; i >= 0; i--) {
    recovered += opens[i] === '{' ? '}' : ']';
  }
  return JSON.parse(recovered);
};



const buildAssessmentReportInputs = ({ reportScopeSummary, workbench, organizationProfile, reportOptions, advisorContent = [], availability = {} }) => {
  const ws = workbench && typeof workbench === 'object' ? workbench : {};
  const mapFinding = (finding = {}) => ({
    title: finding.title || finding.finding || 'Untitled finding',
    area: finding.area || finding.category || '',
    severity: finding.severity || '',
    confidence: finding.confidence || '',
    valuePotential: finding.valuePotential || '',
    effort: finding.effort || '',
    status: finding.status || '',
    sourceArea: finding.sourceArea || finding.source || '',
    evidence: Array.isArray(finding.evidence) ? finding.evidence : (finding.evidence ? [String(finding.evidence)] : []),
    recommendation: finding.recommendation || finding.action || '',
    consultantNotes: finding.consultantNotes || finding.notes || '',
  });
  const mapAction = (action = {}) => ({
    title: action.title || 'Untitled action',
    actionType: action.actionType || '',
    status: action.status || '',
    priority: action.priority || '',
    expectedValue: action.expectedValue || '',
    effort: action.effort || '',
    owner: action.owner || '',
    evidenceSummary: action.evidenceSummary || '',
    implementationSteps: action.implementationSteps || '',
    successMeasure: action.successMeasure || '',
    notes: action.notes || '',
  });
  return {
    report_type: 'client_assessment_report',
    report_options: reportOptions || {},
    section_availability: availability,
    report_scope: reportScopeSummary || '',
    organization: toOrganizationPromptContext(organizationProfile || {}),
    generated_advisor_content: advisorContent,
    workbench: {
      objective: ws.objective || '',
      scope: ws.scope || '',
      businessArea: ws.businessArea || '',
      processOwner: ws.processOwner || '',
      stakeholders: ws.stakeholders || '',
      targetSla: ws.targetSla || '',
      painPoints: ws.painPoints || '',
      workingHypothesis: ws.workingHypothesis || '',
      notes: ws.notes || '',
      findings: Array.isArray(ws.findings) ? ws.findings.map(mapFinding) : [],
      actions: Array.isArray(ws.actions) ? ws.actions.map(mapAction) : [],
      discovery_rules: Array.isArray(ws.discovery_rules) ? ws.discovery_rules : [],
      deliverables: ws.deliverables || {},
    },
  };
};


const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const firstDefined = (...values) => values.find(v => v !== undefined && v !== null && String(v).trim() !== '');

const compactObject = (obj = {}) => Object.fromEntries(
  Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
);

const sortByNumeric = (rows = [], keys = [], dir = 'desc') => [...rows].sort((a, b) => {
  const av = toNumber(keys.map(k => a?.[k]).find(v => v !== undefined), 0);
  const bv = toNumber(keys.map(k => b?.[k]).find(v => v !== undefined), 0);
  return dir === 'asc' ? av - bv : bv - av;
});


const normaliseFieldUsageRows = (fieldUsage) => {
  const rows = Array.isArray(fieldUsage)
    ? fieldUsage
    : (fieldUsage && typeof fieldUsage === 'object'
      ? Object.entries(fieldUsage).map(([field, value]) => ({ field, value }))
      : []);

  const valueLabel = (v) => firstDefined(
    v?.label, v?.name, v?.value, v?.displayValue, v?.display_value, v?.key, v?.id, v?.sys_id, ''
  );
  const valueCount = (v) => firstDefined(
    v?.count, v?.tasks, v?.task_count, v?.workItems, v?.work_items, v?.total, v?.volume, ''
  );
  const valuePct = (v) => firstDefined(
    v?.percentage, v?.percent, v?.pct, v?.share, v?.coverage, ''
  );
  const formatPct = (v) => {
    if (v === undefined || v === null || v === '') return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return n <= 1 ? `${(n * 100).toFixed(1)}%` : `${n.toFixed(1)}%`;
  };
  const formatValues = (values) => {
    if (!values) return '';
    const arr = Array.isArray(values)
      ? values
      : (typeof values === 'object' ? Object.entries(values).map(([k, v]) => (typeof v === 'object' ? { label: k, ...v } : { label: k, count: v })) : [values]);
    return arr.slice(0, 8).map(v => {
      if (v === undefined || v === null) return '';
      if (typeof v !== 'object') return String(v);
      const label = valueLabel(v);
      const count = valueCount(v);
      const pct = formatPct(valuePct(v));
      return [label, count ? `${count}` : '', pct].filter(Boolean).join(' · ');
    }).filter(Boolean).join('; ');
  };

  return rows.map((row = {}) => {
    const values = firstDefined(row.values, row.topValues, row.top_values, row.value, row.distribution, row.breakdown, row.options, '');
    const coverage = firstDefined(row.coverage, row.populatedPct, row.populated_pct, row.populationPercent, row.population_pct, row.percentPopulated, row.percent_populated, '');
    const missing = firstDefined(row.missing, row.missingCount, row.missing_count, row.blankCount, row.blank_count, '');
    return compactObject({
      field: firstDefined(row.field, row.name, row.label, row.column, row.attribute, ''),
      type: firstDefined(row.type, row.source, row.category, ''),
      totalTasks: firstDefined(row.totalTasks, row.total_tasks, row.tasks, row.count, row.total, ''),
      coverage: coverage !== '' ? formatPct(coverage) : '',
      missing,
      topValues: formatValues(values),
    });
  }).filter(r => r.field || r.topValues);
};

const buildEvidencePack = ({
  modelTitle,
  issueTypeStr,
  reportScopeSummary,
  metricsTotal,
  metricsHistory,
  transitionsJSON,
  variantsJSON,
  fieldUsage,
  statusTypeSummary,
  statusTypeMappings,
  activityType,
  activityContext,
  workbench,
  advisorContent,
  selectedOptions,
  organizationProfile,
}) => {
  const transitions = Array.isArray(transitionsJSON) ? transitionsJSON : [];
  const entityCountKey = `${apiProvider.getEntityUpper()}s`;
  const variants = (Array.isArray(variantsJSON) ? variantsJSON : []).map(v => ({ ...v, 'Work items': firstDefined(v['Work items'], v[entityCountKey], v.Tasks, v.tasks, v.count, '') }));
  const metrics = Array.isArray(metricsHistory) ? metricsHistory : [];
  const ws = workbench && typeof workbench === 'object' ? workbench : {};
  const findings = Array.isArray(ws.findings) ? ws.findings : [];
  const actions = Array.isArray(ws.actions) ? ws.actions : [];
  const valueItems = [...findings, ...actions].filter(includesValueSignal);

  const topByVolume = sortByNumeric(transitions, ['Work items', 'TASKS', 'Tasks', 'count']).slice(0, 15);
  const topByDuration = sortByNumeric(transitions, ['Avg deviation (hrs)', 'Avg Duration (hrs)', 'avg_duration_hrs']).slice(0, 15);
  const reworkTransitions = transitions.filter(t => String(t['Is rework?'] ?? t.loop ?? '').toLowerCase() === 'true' || t['Is rework?'] === true).slice(0, 15);
  const topVariants = sortByNumeric(variants, ['Work items', 'Tasks', entityCountKey]).slice(0, 15);
  const longVariants = sortByNumeric(variants, ['Avg Duration (hrs)', 'Std Deviation (hrs)']).slice(0, 15);

  const nodeMap = new Map();
  topByVolume.slice(0, 18).forEach(t => {
    const from = firstDefined(t['From Activity'], t.from_activity, t.from, '');
    const to = firstDefined(t['To Activity'], t.to_activity, t.to, '');
    const count = toNumber(firstDefined(t['Work items'], t.tasks, t.count), 0);
    [from, to].filter(Boolean).forEach(name => {
      const existing = nodeMap.get(name) || 0;
      nodeMap.set(name, existing + count);
    });
  });

  const processMap = {
    type: 'top_transition_map',
    title: 'Process map evidence',
    note: 'Generated from the highest-volume transitions available to the assessment report.',
    nodes: Array.from(nodeMap.entries()).map(([label, volume]) => ({ label, volume })).sort((a,b) => b.volume - a.volume).slice(0, 18),
    edges: topByVolume.slice(0, 18).map(t => ({
      from: firstDefined(t['From Activity'], t.from_activity, t.from, ''),
      to: firstDefined(t['To Activity'], t.to_activity, t.to, ''),
      volume: firstDefined(t['Work items'], t.tasks, t.count, ''),
      avgHours: firstDefined(t['Avg deviation (hrs)'], t['Avg Duration (hrs)'], t.avg_duration_hrs, ''),
      isRework: !!(t['Is rework?'] === true || String(t['Is rework?']).toLowerCase() === 'true'),
    })).filter(e => e.from && e.to),
  };

  const topBottleneck = topByDuration?.[0]
    ? `${topByDuration[0].Transition || [topByDuration[0]['From Activity'], topByDuration[0]['To Activity']].filter(Boolean).join(' → ')}${topByDuration[0]['Avg deviation (hrs)'] ? ` · ${topByDuration[0]['Avg deviation (hrs)']}h` : ''}`
    : '';
  const topVariant = topVariants?.[0] || {};
  const topVariantCoverage = firstDefined(topVariant['% Total TASKS'], topVariant['% Total Tasks'], topVariant.Percent, topVariant.Percentage, '');
  const largestRework = reworkTransitions?.[0]
    ? `${reworkTransitions[0].Transition || [reworkTransitions[0]['From Activity'], reworkTransitions[0]['To Activity']].filter(Boolean).join(' → ')}${reworkTransitions[0]['Work items'] ? ` · ${reworkTransitions[0]['Work items']} items` : ''}`
    : '';

  const metricRow = metrics[0] || {};
  const snapshot = [
    { label: 'Model', value: modelTitle || getProcessModelTitle() || '' },
    { label: 'Scope', value: issueTypeStr || '' },
    { label: 'Work items', value: firstDefined(metricRow[entityCountKey], metricsTotal?.work_items, metricsTotal?.tasks, '') },
    { label: 'Variants', value: firstDefined(metricRow.Variants, metricsTotal?.variants, variants.length || '') },
    { label: 'Activities', value: firstDefined(metricRow.Activities, metricsTotal?.activities, '') },
    { label: 'Steps / transitions', value: firstDefined(metricRow.Steps, metricsTotal?.steps, transitions.length || '') },
    { label: 'Avg transitions', value: firstDefined(metricRow['Avg Transitions'], metricsTotal?.avg_transitions, '') },
    { label: 'Avg duration (hrs)', value: firstDefined(metricRow['Avg Duration'], metricsTotal?.avg_duration_hrs, '') },
    { label: 'Rework %', value: firstDefined(metricRow['% Rework'], metricsTotal?.rework_rate != null ? `${(toNumber(metricsTotal.rework_rate) * 100).toFixed(1)}%` : '') },
    { label: 'Flow efficiency', value: normalisePercentForReport(firstDefined(metricsTotal?.flow_efficiency, metricsTotal?.flowEfficiency, metricsTotal?.flow_efficiency_pct, '')) },
    { label: 'Top bottleneck', value: topBottleneck },
    { label: 'Top variant coverage', value: topVariantCoverage },
    { label: 'Largest rework loop', value: largestRework },
  ].filter(i => i.value !== undefined && i.value !== null && String(i.value).trim() !== '');

  const fieldRows = normaliseFieldUsageRows(fieldUsage);
  const processHealthSummary = extractProcessHealthSummary(advisorContent, metrics, metricsTotal);

  return {
    compiled_at: new Date().toISOString(),
    report_generation_mode: 'comprehensive_compiled_then_ai_narrated',
    selected_options: selectedOptions || {},
    context_usage_note: 'All available evidence, Advisor outputs, curated items and value items were compiled for AI context. selected_options controls report display/default inclusion only.',
    scope_summary: reportScopeSummary || '',
    organization: toOrganizationPromptContext(organizationProfile || {}),
    process_health_summary: {
      ...processHealthSummary,
      metrics: {
        ...(processHealthSummary.metrics || {}),
        topBottleneck,
        topVariantCoverage,
        largestReworkLoop: largestRework,
      },
    },
    canonical_metrics: buildCanonicalReportMetrics({ metricsHistory: metrics, metricsTotal, processHealthSummary }),
    key_metrics: snapshot,
    process_snapshot: snapshot,
    process_map: processMap,
    evidence_tables: {
      metrics: metrics.slice(0, 20),
      top_variants: topVariants,
      longest_variants: longVariants,
      highest_volume_transitions: topByVolume,
      slowest_transitions: topByDuration,
      rework_transitions: reworkTransitions,
      field_usage: fieldRows.slice(0, 30),
      status_type_summary: statusTypeSummary || null,
      status_type_mappings: Array.isArray(statusTypeMappings) ? statusTypeMappings.slice(0, 60) : [],
    },
    advisor_outputs: Array.isArray(advisorContent) ? advisorContent.map(a => ({ key: a.key, label: a.label, text: a.text })) : [],
    curated_findings: findings.map((f, index) => compactObject({
      index: index + 1,
      title: f.title || f.finding,
      area: f.area || f.category,
      severity: f.severity,
      confidence: f.confidence,
      valuePotential: f.valuePotential,
      effort: f.effort,
      status: f.status,
      evidence: Array.isArray(f.evidence) ? f.evidence.join('; ') : f.evidence,
      recommendation: f.recommendation || f.action,
      notes: f.consultantNotes || f.notes,
    })),
    curated_actions: actions.map((a, index) => compactObject({
      index: index + 1,
      title: a.title,
      actionType: a.actionType,
      priority: a.priority,
      status: a.status,
      expectedValue: a.expectedValue,
      effort: a.effort,
      owner: a.owner,
      evidenceSummary: a.evidenceSummary,
      implementationSteps: a.implementationSteps,
      successMeasure: a.successMeasure,
      notes: a.notes,
    })),
    value_items: valueItems.map((item, index) => compactObject({
      index: index + 1,
      title: item.title || item.finding,
      expectedValue: item.expectedValue || item.valuePotential,
      evidence: Array.isArray(item.evidence) ? item.evidence.join('; ') : item.evidence || item.evidenceSummary,
      action: item.action || item.recommendation || item.implementationSteps,
      notes: item.notes || item.consultantNotes,
    })),
    coverage: {
      metrics: metrics.length,
      transitions: transitions.length,
      variants: variants.length,
      advisorOutputs: advisorContent?.length || 0,
      findings: findings.length,
      actions: actions.length,
      valueItems: valueItems.length,
    },
  };
};

const attachCompiledReportArtifacts = (json = {}, evidencePack = {}) => ({
  ...(json || {}),
  compiled_evidence_pack: evidencePack || {},
});


const countSelectedSections = (options = {}) => [
  'includeExecutiveSummary', 'includeAssessmentScope', 'includeOrganizationContext',
  'includeProcessEvidence', 'includeWorkbenchFindings', 'includeWorkbenchActions',
  'includeAdvisorInsights', 'includeValueAnalysis', 'includeRoadmap',
  'includeNextSteps', 'includeEvidenceAppendix',
].filter(k => options[k]).length;


const stripHtmlToText = (html = '') => String(html || '')
  .replace(/<script[\s\S]*?<\/script>/gi, ' ')
  .replace(/<style[\s\S]*?<\/style>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&quot;/g, '"')
  .replace(/&#39;/g, "'")
  .replace(/\s+/g, ' ')
  .trim();

const truncateText = (text = '', max = 6000) => {
  const s = String(text || '').trim();
  return s.length > max ? `${s.slice(0, max)}…` : s;
};


// Forge bridge invocations fail at ~200KB total payload. The prompt is not the
// whole payload: billing authorization, model settings and JSON escaping add
// overhead, so keep the assessment-report prompt comfortably below the limit.
const FORGE_ASSESSMENT_REPORT_PROMPT_TARGET = 135000;
const FORGE_ASSESSMENT_REPORT_INVOKE_SAFE_BYTES = 175 * 1024;

const estimateUtf8Bytes = (value) => {
  const str = typeof value === 'string' ? value : JSON.stringify(value || {});
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str).length;
  return unescape(encodeURIComponent(str)).length;
};

const fitForgeAssessmentPromptForInvoke = (prompt, payloadExtras = {}) => {
  let nextPrompt = String(prompt || '');
  const makePayload = () => ({
    prompt: nextPrompt,
    ...payloadExtras,
    // Conservative allowance for billingAuthorization, bridge metadata and
    // JSON escaping overhead added inside invokeProcessOpenAIWithBilling.
    billingAuthorization: {
      source: 'assessment-credit-or-subscription',
      modelId: 'x'.repeat(64),
      recordCount: 999999,
      recordLimit: 50000,
      requiredCredits: 1,
      assessmentCreditId: 'x'.repeat(64),
    },
  });

  let bytes = estimateUtf8Bytes(makePayload());
  if (bytes <= FORGE_ASSESSMENT_REPORT_INVOKE_SAFE_BYTES) return nextPrompt;

  // Reduce by byte ratio rather than character count. The loop handles JSON
  // escaping and any non-ASCII text safely.
  for (let i = 0; i < 6 && bytes > FORGE_ASSESSMENT_REPORT_INVOKE_SAFE_BYTES; i += 1) {
    const ratio = FORGE_ASSESSMENT_REPORT_INVOKE_SAFE_BYTES / Math.max(bytes, 1);
    const targetChars = Math.max(60000, Math.floor(nextPrompt.length * ratio * 0.94));
    nextPrompt = truncateText(nextPrompt, targetChars);
    bytes = estimateUtf8Bytes(makePayload());
  }

  if (bytes > FORGE_ASSESSMENT_REPORT_INVOKE_SAFE_BYTES) {
    nextPrompt = truncateText(nextPrompt, 90000);
  }
  return nextPrompt;
};

const list = (value) => Array.isArray(value) ? value.filter(Boolean) : [];

const limitRows = (rows, max, textLimit = 260) => list(rows).slice(0, max).map(row => {
  if (!row || typeof row !== 'object') return row;
  return Object.fromEntries(Object.entries(row).map(([key, value]) => {
    if (typeof value === 'string') return [key, truncateText(value, textLimit)];
    if (Array.isArray(value)) return [key, value.slice(0, 8).map(v => typeof v === 'string' ? truncateText(v, textLimit) : v)];
    return [key, value];
  }));
});

const compactForgeAssessmentEvidencePack = (pack = {}) => {
  const tables = pack.evidence_tables || {};
  const map = pack.process_map || {};
  return compactObject({
    compiled_at: pack.compiled_at,
    report_generation_mode: 'forge_prompt_compact_full_evidence_attached_after_generation',
    scope_summary: truncateText(pack.scope_summary, 1200),
    organization: pack.organization,
    process_health_summary: pack.process_health_summary ? {
      ...pack.process_health_summary,
      excerpt: truncateText(pack.process_health_summary.excerpt, 1400),
      verdict: truncateText(pack.process_health_summary.verdict, 700),
    } : undefined,
    canonical_metrics: pack.canonical_metrics,
    key_metrics: limitRows(pack.key_metrics, 16, 180),
    process_snapshot: limitRows(pack.process_snapshot, 16, 180),
    process_map: {
      ...map,
      nodes: limitRows(map.nodes, 10, 140),
      edges: limitRows(map.edges, 12, 160),
    },
    evidence_tables: {
      metrics: limitRows(tables.metrics, 1, 180),
      top_variants: limitRows(tables.top_variants, 8, 180),
      longest_variants: limitRows(tables.longest_variants, 5, 180),
      highest_volume_transitions: limitRows(tables.highest_volume_transitions, 8, 180),
      slowest_transitions: limitRows(tables.slowest_transitions, 8, 180),
      rework_transitions: limitRows(tables.rework_transitions, 8, 180),
      field_usage: limitRows(tables.field_usage, 8, 240),
      status_type_summary: tables.status_type_summary ? truncateText(JSON.stringify(tables.status_type_summary), 1200) : null,
      status_type_mappings: limitRows(tables.status_type_mappings, 15, 140),
    },
    advisor_outputs: list(pack.advisor_outputs).map(a => ({
      key: a.key,
      label: a.label,
      text: truncateText(a.text, 1200),
    })),
    curated_findings: limitRows(pack.curated_findings, 8, 420),
    curated_actions: limitRows(pack.curated_actions, 8, 420),
    value_items: limitRows(pack.value_items, 6, 360),
    coverage: pack.coverage,
  });
};

const buildForgeAssessmentPromptArgs = ({
  inputEvidence,
  compiledEvidencePack,
  assessmentReportInputs,
  issueTypeStr,
  metricsHistory,
  metricsTotal,
  transitionsJSON,
  variantsJSON,
  fieldUsage,
  statusTypeSummary,
  statusTypeMappings,
  activityType,
  activityContext,
  appVersion,
  sourceSystem,
  reportScopeSummary,
}) => {
  const compactPack = compactForgeAssessmentEvidencePack(compiledEvidencePack);
  const compactAssessmentInputs = compactObject({
    ...(assessmentReportInputs || {}),
    compiled_evidence_pack: compactPack,
    advisorContent: list(assessmentReportInputs?.advisorContent).map(a => ({
      key: a.key,
      label: a.label,
      generated: a.generated,
      text: truncateText(a.text, 1100),
    })),
  });

  return {
    inputPayload: {
      ...inputEvidence,
      sampleItems: [],
      rows: [],
      rawRows: [],
      assessment_report_inputs: compactAssessmentInputs,
      compiled_evidence_pack: compactPack,
    },
    issueTypeStr,
    metricsHistory: list(metricsHistory).slice(0, 1),
    metricsTotal,
    transitionsJSON: limitRows(transitionsJSON, 10, 180),
    variantsJSON: limitRows(variantsJSON, 8, 180),
    sampleItems: [],
    fieldUsage: limitRows(fieldUsage, 8, 240),
    statusTypeSummary: statusTypeSummary ? truncateText(JSON.stringify(statusTypeSummary), 1200) : statusTypeSummary,
    statusTypeMappings: limitRows(statusTypeMappings, 15, 140),
    activityType,
    activityContext: activityContext ? truncateText(JSON.stringify(activityContext), 1400) : activityContext,
    appVersion,
    sourceSystem,
    reportScopeSummary: truncateText(reportScopeSummary, 1600),
  };
};


const normalisePercentForReport = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return '';
  const raw = String(value).trim();
  const n = Number(raw.replace('%', ''));
  if (!Number.isFinite(n)) return raw;
  // Some upstream values are stored as fractions; display percentages consistently.
  const pct = n > 0 && n <= 1 && !raw.includes('%') ? n * 100 : n;
  return `${pct.toFixed(1)}%`;
};

const normaliseHoursForReport = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return '';
  const n = Number(String(value).replace(/hrs?|hours?/gi, '').trim());
  return Number.isFinite(n) ? `${n.toFixed(1)}h` : String(value).trim();
};


const extractPercentFromText = (text = '', patterns = []) => {
  const source = String(text || '');
  for (const pattern of patterns) {
    const match = source.match(pattern);
    if (match?.[1]) return normalisePercentForReport(match[1]);
  }
  return '';
};

const buildCanonicalReportMetrics = ({ metricsHistory = [], metricsTotal = null, processHealthSummary = {} }) => {
  const row = metricsHistory?.[0] || {};
  const getMetric = (...keys) => firstDefined(...keys.map(k => row?.[k]), '');
  const healthText = [processHealthSummary?.excerpt, processHealthSummary?.verdict, processHealthSummary?.diagnosis].filter(Boolean).join(' ');
  const parsedFlowEfficiency = extractPercentFromText(healthText, [
    /flow\s+efficiency[^0-9]{0,30}(\d+(?:\.\d+)?)%/i,
    /(\d+(?:\.\d+)?)%\s+flow\s+efficiency/i,
  ]);
  const canonical = compactObject({
    processHealthScore: processHealthSummary?.scoreLabel || processHealthSummary?.score || '',
    processHealthConfidence: processHealthSummary?.confidence || '',
    workItems: getMetric(`${apiProvider.getEntityUpper()}s`, 'Work items', 'Tasks') || firstDefined(metricsTotal?.work_items, metricsTotal?.tasks, ''),
    variants: getMetric('Variants') || firstDefined(metricsTotal?.variants, ''),
    activities: getMetric('Activities') || firstDefined(metricsTotal?.activities, ''),
    transitions: getMetric('Steps') || firstDefined(metricsTotal?.steps, ''),
    avgTransitions: getMetric('Avg Transitions') || firstDefined(metricsTotal?.avg_transitions, ''),
    avgDurationHours: normaliseHoursForReport(getMetric('Avg Duration') || firstDefined(metricsTotal?.avg_duration_hrs, '')),
    reworkPercent: normalisePercentForReport(getMetric('% Rework') || firstDefined(metricsTotal?.rework_percent, metricsTotal?.rework_pct, metricsTotal?.rework_rate, '')),
    flowEfficiency: normalisePercentForReport(firstDefined(metricsTotal?.flow_efficiency, metricsTotal?.flowEfficiency, metricsTotal?.flow_efficiency_pct, processHealthSummary?.metrics?.flowEfficiency, parsedFlowEfficiency, '')),
  });
  return canonical;
};

const REPORT_SCAFFOLDING_PATTERNS = [
  /Process Health is mandatory for the Assessment Report[\s\S]{0,220}?(?:\.|$)/gi,
  /Consultant-selected findings are treated as first-class assessment content[\s\S]{0,220}?(?:\.|$)/gi,
  /They are included early so the report reads as a deliverable, not just a data appendix\.?/gi,
  /This section is compiled deterministically from the model evidence before the AI narrative is written\.?/gi,
  /It is intentionally comprehensive so the consultant can remove rows or sections after generation\.?/gi,
  /Raw Advisor outputs are kept as source material rather than presented as the main report narrative\.?/gi,
  /Generated Advisor content was available and used as source context for this report\.?/gi,
  /Use this as a supporting advisory signal alongside objective evidence and curated findings\.?/gi,
  /selected_options controls report display\/default inclusion only\.?/gi,
  /All available evidence, Advisor outputs, curated items and value items were compiled for AI context\.?/gi,
];

const cleanReportText = (value = '', canonical = {}) => {
  let text = String(value || '');
  REPORT_SCAFFOLDING_PATTERNS.forEach(pattern => { text = text.replace(pattern, ''); });
  text = text
    .replace(/\bdestructive\s+rework\s+loops?\b/gi, 'regressive rework cycles')
    .replace(/\bdestructive\s+loop\b/gi, 'regressive cycle')
    .replace(/\bWhy Now\b/g, 'Strategic Driver')
    .replace(/\bExpected benefit\s*\/\s*success measure\b/gi, 'Target / Success Measure')
    .replace(/0\s*[–-]\s*3\s*Months/gi, 'Month 1')
    .replace(/3\s*[–-]\s*6\s*Months/gi, 'Months 2-3')
    .replace(/6\s*[–-]\s*12\s*Months/gi, 'Months 4-6')
    .replace(/Quick Healthcheck\s+[—-]\s+[^.]{0,80}Quick Healthcheck\s*/gi, 'Quick Healthcheck ')
    .replace(/Process Health\s+[—-]\s+[^.]{0,80}Process Health\s*/gi, 'Process Health ')
    .replace(/AI, Automation & Optimisation\s+[—-]\s+[^.]{0,80}AI, Automation & Optimisation\s*/gi, 'AI, Automation & Optimisation ')
    .replace(/Execution Strategy\s+[—-]\s+[^.]{0,80}Execution Strategy\s*/gi, 'Execution Strategy ')
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (canonical.flowEfficiency) {
    text = text.replace(/(?:reports?\s+)?(?:only\s+)?\d+(?:\.\d+)?%\s+flow efficiency/gi, `${canonical.flowEfficiency} flow efficiency`);
    text = text.replace(/reports?\s+only\s+\d+(?:\.\d+)?%\s+flow\s+efficiency/gi, `reports ${canonical.flowEfficiency} flow efficiency`);
    text = text.replace(/flow efficiency\s+(?:of\s+)?(?:only\s+)?\d+(?:\.\d+)?%/gi, `flow efficiency of ${canonical.flowEfficiency}`);
  }
  if (canonical.reworkPercent) {
    text = text.replace(/\b\d+(?:\.\d+)?%\s+rework\s+rate\b/gi, `${canonical.reworkPercent} rework rate`);
    text = text.replace(/rework\s+rate\s+(?:of\s+)?\d+(?:\.\d+)?%/gi, `rework rate of ${canonical.reworkPercent}`);
  }
  return text;
};

const recursivelyCleanReportObject = (value, canonical = {}) => {
  if (typeof value === 'string') return cleanReportText(value, canonical);
  if (Array.isArray(value)) return value.map(item => recursivelyCleanReportObject(item, canonical));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, recursivelyCleanReportObject(v, canonical)]));
  }
  return value;
};

const enforceStrictReportSchema = (json = {}, evidencePack = {}) => {
  const canonical = evidencePack?.canonical_metrics || buildCanonicalReportMetrics({
    metricsHistory: evidencePack?.evidence_tables?.metrics || [],
    metricsTotal: null,
    processHealthSummary: evidencePack?.process_health_summary || {},
  });
  const cleaned = recursivelyCleanReportObject(json || {}, canonical);
  const health = evidencePack?.process_health_summary || {};
  cleaned.process_health_summary = {
    ...(cleaned.process_health_summary || {}),
    score: health.score || cleaned.process_health_summary?.score || canonical.processHealthScore || '',
    score_label: health.scoreLabel || cleaned.process_health_summary?.score_label || canonical.processHealthScore || '',
    confidence: health.confidence || cleaned.process_health_summary?.confidence || canonical.processHealthConfidence || '',
    diagnosis: cleanReportText(cleaned.process_health_summary?.diagnosis || health.verdict || health.excerpt || '', canonical),
    score_rationale: cleanReportText(cleaned.process_health_summary?.score_rationale || health.verdict || '', canonical),
    canonical_metrics: canonical,
  };
  if (Array.isArray(cleaned.headline_signals)) {
    cleaned.headline_signals = cleaned.headline_signals.map(signal => {
      const label = String(signal?.label || '').toLowerCase();
      if (label.includes('flow') && canonical.flowEfficiency) return { ...signal, value: canonical.flowEfficiency };
      if (label.includes('rework') && canonical.reworkPercent) return { ...signal, value: canonical.reworkPercent };
      if (label.includes('variant') && canonical.variants) return { ...signal, value: String(canonical.variants) };
      if ((label.includes('work item') || label.includes('volume')) && canonical.workItems) return { ...signal, value: String(canonical.workItems) };
      return signal;
    });
  }
  return cleaned;
};

const prepareReportForRendering = (json = {}, evidencePack = {}) => {
  const constrained = enforceStrictReportSchema(json, evidencePack);
  const cleanedPack = recursivelyCleanReportObject(evidencePack || {}, evidencePack?.canonical_metrics || {});
  return attachCompiledReportArtifacts(constrained, cleanedPack);
};

const getStoredGeneratedContent = ({ modelId, column }) => {
  const cached = getCachedConsultantContent({ modelId, column });
  const dbHtml = getAiConsultantContent(column);
  const html = cached || (isHtmlCurrentVersion(dbHtml) ? dbHtml : null);
  if (!html) return null;
  return {
    html,
    text: truncateText(stripHtmlToText(html), 5500),
  };
};

const buildAdvisorContentPackage = (modelId) => ADVISOR_CONTENT_SECTIONS
  .map(section => {
    const stored = getStoredGeneratedContent({ modelId, column: section.column });
    if (!stored?.text) return null;
    return {
      key: section.key,
      label: section.label,
      column: section.column,
      generated: true,
      text: stored.text,
    };
  })
  .filter(Boolean);

const extractProcessHealthSummary = (advisorContent = [], metricsHistory = [], metricsTotal = null) => {
  const processHealth = (advisorContent || []).find(a => a?.key === 'processHealth');
  const quickHealth = (advisorContent || []).find(a => a?.key === 'quickHealthcheck');
  const text = String(processHealth?.text || quickHealth?.text || '');
  const scoreMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:out of\s*)?\/?\s*10\s*(?:Process\s*)?Health\s*Score/i)
    || text.match(/Health\s*Score[^\d]*(\d+(?:\.\d+)?)/i);
  const confidenceMatch = text.match(/Confidence[:\s]+(High|Moderate|Low)/i)
    || text.match(/(High|Moderate|Low)\s+confidence/i);
  const verdictMatch = text.match(/Process Health\s+([^\n]{20,180})/i);
  let verdictRaw = verdictMatch ? verdictMatch[1] : '';
  // The raw tab text often reads "Process Health — <Model Title> Process Health <actual verdict>";
  // the first match then captures the title prefix. Take the text after the LAST
  // "Process Health" occurrence and strip leading dashes/separators.
  const phIdx = verdictRaw.toLowerCase().lastIndexOf('process health');
  if (phIdx > -1) verdictRaw = verdictRaw.slice(phIdx + 'process health'.length);
  verdictRaw = verdictRaw.replace(/^[\s\u2014\u2013:.-]+/, '');
  const cleanVerdict = verdictRaw
    ? cleanReportText(verdictRaw)
        .replace(/\s+T$/, '')
        .replace(/\s+[A-Za-z]{1,2}$/, '')
        .trim()
    : '';
  const parsedFlowEfficiency = extractPercentFromText(text, [
    'flow efficiency',
    'flow_efficiency',
    'flowEfficiency',
  ]);
  const metricRow = metricsHistory?.[0] || {};
  const getMetric = (...keys) => firstDefined(...keys.map(k => metricRow?.[k]), '');
  return compactObject({
    available: !!processHealth?.text,
    source: processHealth?.label || 'Process Health Assessment',
    score: scoreMatch ? Number(scoreMatch[1]).toFixed(1) : '',
    scoreLabel: scoreMatch ? `${Number(scoreMatch[1]).toFixed(1)} / 10` : '',
    confidence: confidenceMatch ? confidenceMatch[1] : '',
    verdict: cleanVerdict,
    excerpt: truncateText(text, 2400),
    metrics: compactObject({
      workItems: getMetric(`${apiProvider.getEntityUpper()}s`, 'Work items', 'Tasks'),
      variants: getMetric('Variants'),
      activities: getMetric('Activities'),
      transitions: getMetric('Steps'),
      avgTransitions: getMetric('Avg Transitions'),
      avgDurationHours: getMetric('Avg Duration'),
      reworkPercent: getMetric('% Rework'),
      flowEfficiency: normalisePercentForReport(firstDefined(metricsTotal?.flow_efficiency, metricsTotal?.flowEfficiency, metricsTotal?.flow_efficiency_pct, parsedFlowEfficiency, '')),
    }),
  });
};

const hasText = (value) => String(value || '').trim().length > 0;
const includesValueSignal = (item = {}) => {
  const haystack = [item.sourceArea, item.area, item.title, item.finding, item.recommendation, item.action, item.expectedValue, item.notes]
    .map(v => String(v || '').toLowerCase()).join(' ');
  return haystack.includes('value') || haystack.includes('saving') || haystack.includes('cost') || haystack.includes('roi') || haystack.includes('benefit');
};

const buildAvailabilityMap = ({ reportScopeSummary, organizationProfile, orgFields, metricsHistory, transitions, variants, workbench, advisorContent }) => {
  const ws = workbench && typeof workbench === 'object' ? workbench : {};
  const findings = Array.isArray(ws.findings) ? ws.findings : [];
  const actions = Array.isArray(ws.actions) ? ws.actions : [];
  const valueItems = [...findings, ...actions].filter(includesValueSignal);
  const evidenceCount = (metricsHistory?.length || 0) + (transitions?.length || 0) + (variants?.length || 0);
  const scopeReady = hasText(reportScopeSummary) || hasText(getProcessModelTitle()) || evidenceCount > 0;
  const orgReady = (orgFields?.length || 0) > 0 || !!toOrganizationPromptContext(organizationProfile || {})?.available;
  const advisorCount = advisorContent?.length || 0;
  const hasProcessHealth = (advisorContent || []).some(a => a?.key === 'processHealth' && hasText(a?.text));
  const hasCurated = findings.length > 0 || actions.length > 0;

  return {
    scope: { status: scopeReady ? 'ready' : 'missing', count: scopeReady ? 1 : 0, message: scopeReady ? 'Model scope available' : 'Model scope not available' },
    organization: { status: orgReady ? 'ready' : 'optional', count: orgFields?.length || 0, message: orgReady ? 'Organisation details available' : 'Optional — add organisation details for more tailored wording' },
    evidence: { status: evidenceCount > 0 ? 'ready' : 'missing', count: evidenceCount, message: evidenceCount > 0 ? 'Metrics/transitions/variants available' : 'Process evidence not available yet' },
    advisor: { status: hasProcessHealth ? 'ready' : 'missing', count: advisorCount, message: hasProcessHealth ? `Process Health ready · ${advisorCount} generated Advisor section${advisorCount === 1 ? '' : 's'}` : 'Generate Process Health before the assessment report' },
    findings: { status: findings.length > 0 ? 'ready' : 'optional', count: findings.length, message: findings.length > 0 ? `${findings.length} curated finding${findings.length === 1 ? '' : 's'}` : 'Optional — add curated findings to enrich the report' },
    actions: { status: actions.length > 0 ? 'ready' : 'optional', count: actions.length, message: actions.length > 0 ? `${actions.length} curated action${actions.length === 1 ? '' : 's'}` : 'Optional — add curated actions to enrich the report' },
    value: { status: valueItems.length > 0 ? 'ready' : 'optional', count: valueItems.length, message: valueItems.length > 0 ? `${valueItems.length} value-related item${valueItems.length === 1 ? '' : 's'}` : 'Optional — save value analysis as a finding/action' },
    roadmap: { status: (actions.length > 0 || advisorCount > 0) ? 'ready' : 'limited', count: actions.length, message: actions.length > 0 ? 'Roadmap can use curated actions' : 'Can generate, but stronger with actions or strategy content' },
    nextSteps: { status: hasCurated || advisorCount > 0 ? 'ready' : 'limited', count: (findings.length + actions.length + advisorCount), message: hasCurated ? 'Next steps can use curated assessment content' : 'Can generate, but stronger with curated findings/actions' },
    appendix: { status: evidenceCount > 0 ? 'ready' : 'missing', count: evidenceCount, message: evidenceCount > 0 ? 'Evidence appendix available' : 'No evidence available for appendix' },
    executiveSummary: { status: (evidenceCount > 0 || advisorCount > 0 || hasCurated) ? 'ready' : 'missing', count: 1, message: 'Synthesised from all available assessment context' },
  };
};

const getStatusMeta = (status) => {
  switch (status) {
    case 'ready': return { label: 'Ready', color: '#00875A', bg: '#E3FCEF', icon: CheckCircle2 };
    case 'optional': return { label: 'Optional', color: '#6B778C', bg: '#F4F5F7', icon: Circle };
    case 'limited': return { label: 'Limited', color: '#974F0C', bg: '#FFF7D6', icon: AlertTriangle };
    default: return { label: 'Missing', color: '#DE350B', bg: '#FFEBE6', icon: AlertTriangle };
  }
};

const buildSectionRows = ({ reportOptions, availability }) => REPORT_SECTION_DEFS.map(def => {
  const source = availability?.[def.sourceKey] || { status: 'missing', message: 'Not available', count: 0 };
  const selectable = source.status === 'ready' || source.status === 'optional' || source.status === 'limited';
  const selected = !!reportOptions?.[def.optionKey] && selectable;
  return { ...def, source, selectable, selected };
});

const reportCheckboxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  border: '1px solid #DFE1E6',
  borderRadius: 8,
  background: '#fff',
  fontSize: 13,
  color: '#172B4D',
  fontWeight: 600,
};

const miniLinkButtonStyle = {
  border: '1px solid #B3D4FF',
  background: '#FFFFFF',
  color: '#0C66E4',
  borderRadius: 6,
  padding: '4px 7px',
  fontSize: 11,
  fontWeight: 800,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
};

const formatOrgFields = (profile = {}) => {
  const ctx = toOrganizationPromptContext(profile);
  return ctx.available ? ctx.fields : [];
};


const previewCardStyle = {
  border: '1px solid #DFE1E6',
  borderRadius: 10,
  padding: 14,
  background: '#FFFFFF',
  minHeight: 360,
};

const previewItemStyle = {
  border: '1px solid #EBECF0',
  borderRadius: 8,
  padding: '8px 10px',
  background: '#FAFBFC',
};

const previewItemLabelStyle = {
  fontSize: 10,
  color: '#6B778C',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  marginBottom: 3,
};

const previewItemValueStyle = {
  fontSize: 12,
  color: '#172B4D',
  lineHeight: 1.45,
  fontWeight: 600,
};

const formatPreviewValue = (value) => {
  if (value === undefined || value === null || value === '') return 'Not available';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const rowFromObject = (obj = {}, keys = [], fallbackLabel = 'Item') => {
  const label = firstDefined(...keys.map(k => obj?.[k]), fallbackLabel);
  return formatPreviewValue(label);
};

const buildSectionPreview = ({
  row,
  reportSectionRows,
  modelTitle,
  reportScopeSummary,
  orgFields,
  metricsHistory,
  transitions,
  variants,
  advisorContent,
  workbench,
}) => {
  const ws = workbench && typeof workbench === 'object' ? workbench : {};
  const findings = Array.isArray(ws.findings) ? ws.findings : [];
  const actions = Array.isArray(ws.actions) ? ws.actions : [];
  const valueItems = [...findings, ...actions].filter(includesValueSignal);
  const metricRow = metricsHistory?.[0] || {};
  const topTransitions = sortByNumeric(transitions || [], ['Work items', 'TASKS', 'Tasks', 'count']).slice(0, 5);
  const slowTransitions = sortByNumeric(transitions || [], ['Avg deviation (hrs)', 'Avg Duration (hrs)', 'avg_duration_hrs']).slice(0, 5);
  const topVariants = sortByNumeric(variants || [], ['Work items', 'Tasks', `${apiProvider.getEntityUpper()}s`]).slice(0, 5);
  const readyLabels = (reportSectionRows || []).filter(r => r.selected).map(r => r.label);

  const base = {
    title: row?.label || 'Section preview',
    subtitle: row?.description || '',
    status: row?.source?.message || '',
    items: [],
    note: '',
  };

  switch (row?.sourceKey) {
    case 'executiveSummary':
      return {
        ...base,
        note: 'This section is written by AI from all available assessment context. Section selections control visible output, but the executive narrative can still use evidence, Advisor content, curated findings/actions, and value items that are available.',
        items: [
          { label: 'Visible sections selected', value: readyLabels.length ? readyLabels.join(', ') : 'No sections selected' },
          { label: 'Evidence available', value: `${metricsHistory?.length || 0} metric row(s), ${transitions?.length || 0} transition(s), ${variants?.length || 0} variant(s)` },
          { label: 'Curated content', value: `${findings.length} finding(s), ${actions.length} action(s)` },
        ],
      };
    case 'scope':
      return {
        ...base,
        items: [
          { label: 'Model', value: modelTitle || getProcessModelTitle() || 'Not available' },
          { label: 'Scope summary', value: reportScopeSummary || 'Not available' },
          { label: 'Work items', value: firstDefined(metricRow[`${apiProvider.getEntityUpper()}s`], metricRow.WorkItems, metricRow.work_items, 'Not available') },
          { label: 'Variants', value: firstDefined(metricRow.Variants, variants?.length, 'Not available') },
          { label: 'Steps / transitions', value: firstDefined(metricRow.Steps, transitions?.length, 'Not available') },
        ],
      };
    case 'organization':
      return {
        ...base,
        note: orgFields?.length ? 'These saved organisation details will be used to tailor report language and recommendations.' : 'No organisation details are saved. The report can still be generated, but tailoring will be weaker.',
        items: (orgFields || []).slice(0, 10).map(({ field, value }) => ({ label: field, value })),
      };
    case 'evidence':
      return {
        ...base,
        items: [
          { label: 'Metric snapshot', value: Object.keys(metricRow).length ? Object.entries(metricRow).slice(0, 6).map(([k, v]) => `${k}: ${v}`).join(' · ') : 'No metrics available' },
          { label: 'Top variants', value: topVariants.length ? topVariants.map(v => rowFromObject(v, ['Variant', 'variant', 'ID'])).join(' | ') : 'No variant data available' },
          { label: 'Highest-volume transitions', value: topTransitions.length ? topTransitions.map(t => `${firstDefined(t['From Activity'], t.from_activity, '')} → ${firstDefined(t['To Activity'], t.to_activity, '')} (${firstDefined(t['Work items'], t.tasks, t.count, 'n/a')})`).join(' | ') : 'No transition data available' },
          { label: 'Slowest transitions', value: slowTransitions.length ? slowTransitions.map(t => `${firstDefined(t['From Activity'], t.from_activity, '')} → ${firstDefined(t['To Activity'], t.to_activity, '')} (${firstDefined(t['Avg deviation (hrs)'], t['Avg Duration (hrs)'], t.avg_duration_hrs, 'n/a')} hrs)`).join(' | ') : 'No duration data available' },
        ],
      };
    case 'advisor':
      return {
        ...base,
        note: advisorContent?.length ? 'The report will reuse the generated Advisor content listed below.' : 'Generate Advisor content first if you want AI health, opportunity, strategy, or framework sections included.',
        items: (advisorContent || []).map(a => ({ label: a.label, value: truncateText(a.text, 280) })),
      };
    case 'findings':
      return {
        ...base,
        note: findings.length ? 'Curated findings will be included directly and used to strengthen the executive summary, recommendations, roadmap, and next steps.' : 'No curated findings exist yet. Add findings in Assessment before generating if these should appear in the report.',
        items: findings.slice(0, 8).map((f, i) => ({ label: `Finding ${i + 1}`, value: `${f.title || f.finding || 'Untitled finding'}${f.severity ? ` · ${f.severity}` : ''}${f.evidence ? ` · ${Array.isArray(f.evidence) ? f.evidence.join('; ') : f.evidence}` : ''}` })),
      };
    case 'actions':
      return {
        ...base,
        note: actions.length ? 'Curated actions will be included directly and used to generate the implementation roadmap.' : 'No curated actions exist yet. Add actions in Assessment before generating if these should appear in the report.',
        items: actions.slice(0, 8).map((a, i) => ({ label: `Action ${i + 1}`, value: `${a.title || 'Untitled action'}${a.priority ? ` · ${a.priority}` : ''}${a.owner ? ` · Owner: ${a.owner}` : ''}${a.expectedValue ? ` · Value: ${a.expectedValue}` : ''}` })),
      };
    case 'value':
      return {
        ...base,
        note: valueItems.length ? 'Value-related findings/actions will be used in the Value Opportunity section.' : 'No value analysis has been saved as a finding or action. Run Value and save the relevant value items if required.',
        items: valueItems.slice(0, 8).map((item, i) => ({ label: `Value item ${i + 1}`, value: `${item.title || item.finding || 'Value item'}${item.expectedValue || item.valuePotential ? ` · ${item.expectedValue || item.valuePotential}` : ''}` })),
      };
    case 'roadmap':
      return {
        ...base,
        note: 'The roadmap is generated from curated actions first, then Advisor execution strategy, findings, and next-step content.',
        items: actions.slice(0, 6).map((a, i) => ({ label: `Roadmap input ${i + 1}`, value: `${a.title || 'Untitled action'}${a.priority ? ` · ${a.priority}` : ''}${a.effort ? ` · Effort: ${a.effort}` : ''}` })),
      };
    case 'nextSteps':
      return {
        ...base,
        note: 'Next steps are generated from selected sections, missing content warnings, curated findings/actions, and the recommended implementation path.',
        items: [
          { label: 'Potential next-step inputs', value: `${findings.length} finding(s), ${actions.length} action(s), ${advisorContent?.length || 0} Advisor output(s)` },
          { label: 'Typical output', value: 'Confirm priorities, agree improvement sprint, implement high-value changes, monitor impact, and rerun the assessment.' },
        ],
      };
    case 'appendix':
      return {
        ...base,
        note: 'The appendix should be comprehensive by default so the consultant can remove evidence rather than manually add it later.',
        items: [
          { label: 'Metrics rows', value: metricsHistory?.length || 0 },
          { label: 'Transition rows', value: transitions?.length || 0 },
          { label: 'Variant rows', value: variants?.length || 0 },
          { label: 'Appendix content', value: 'Process snapshot, top variants, slowest transitions, highest-volume transitions, rework/loop transitions, field/attribute evidence, and source evidence tables.' },
        ],
      };
    default:
      return base;
  }
};

// ── ConsultingReport ──────────────────────────────────────────────────────────

const ConsultingReport = ({
  AI,
  reportState,
  setReportState,
  license,
  licenseType,
  aiModelConfig,
  processLabel,
  entityLabel,
}) => {
  // ── Local state ───────────────────────────────────────────────────────────
  const [report,          setReportLocal]          = useState(reportState?.report          || null);
  const [reportGenerated, setReportGeneratedLocal] = useState(reportState?.reportGenerated || false);
  const [modelTitle,      setModelTitleLocal]      = useState(reportState?.modelTitle      || '');

  const [savedHtml, setSavedHtml] = useState(null);

  const setReport = (v) => {
    setReportLocal(v);
    if (setReportState) setReportState(prev => ({ ...prev, report: v }));
  };
  const setReportGenerated = (v) => {
    setReportGeneratedLocal(v);
    if (setReportState) setReportState(prev => ({ ...prev, reportGenerated: v }));
  };
  const setModelTitle = (v) => {
    setModelTitleLocal(v);
    if (setReportState) setReportState(prev => ({ ...prev, modelTitle: v }));
  };

  const [isExportModalOpen,   setIsExportModalOpen]   = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotPanelOpen, setSnapshotPanelOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  const [organizationProfile, setOrganizationProfile] = useState({});
  const [reportOptions, setReportOptions] = useState(DEFAULT_REPORT_OPTIONS);
  const [previewSectionKey, setPreviewSectionKey] = useState('includeProcessEvidence');

  // ── Export handlers ───────────────────────────────────────────────────────
  const handleExportHTML = (themeId = appDefaultReportTheme()) => {
    if (!report && !savedHtml) return;
    // Always regenerate export HTML to ensure it has the latest renderer updates (e.g., headers)
    const html = report ? buildConsultingReportExportHTML(report, modelTitle, { theme: themeId }) : savedHtml;
    const blob = new Blob([html], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `assessment-report-${(modelTitle || 'report').replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setIsExportModalOpen(false);
  };

  const handleExportPPTX = async (themeId = appDefaultReportTheme()) => {
    // PPTX needs the structured report object; saved HTML snapshots can't be mapped to slides.
    if (!report) return;
    try {
      // Lazy import keeps pptxgenjs (~320KB) out of the main bundle until first use.
      const { buildConsultingReportPptx } = await import('@process-advisor/process-miner/consultant/consultingReport-pptx');
      // theme drives colours, backgrounds and the wordmark suffix ('for ServiceNow' / 'for Jira').
      const pptx = buildConsultingReportPptx(report, modelTitle, { theme: themeId });
      await pptx.writeFile({ fileName: `assessment-report-${(modelTitle || 'report').replace(/\s+/g, '-').toLowerCase()}.pptx` });
    } catch (error) {
      console.error('PPTX export failed', error);
    }
    setIsExportModalOpen(false);
  };

  const handleExportPDF = (themeId = appDefaultReportTheme()) => {
    if (!report && !savedHtml) return;
    // Always regenerate export HTML to ensure it has the latest renderer updates (e.g., headers)
    const html = report ? buildConsultingReportExportHTML(report, modelTitle, { theme: themeId }) : savedHtml;
    // Inject a print-only header then render via hidden iframe
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    const printHeader = `
      <style>
        @media print {
          .pdf-header {
            display: flex !important;
            flex-direction: column;
            align-items: stretch;
            gap: 4px;
            padding: 12px 24px 14px 24px;
            border-bottom: 2px solid #0052cc;
            margin-bottom: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
          .pdf-header-top { display: flex; justify-content: space-between; align-items: baseline; }
          .pdf-header-title { font-size: 13px; font-weight: 700; color: #172b4d; }
          .pdf-header-meta  { font-size: 11px; color: #5e6c84; white-space: nowrap; }
          .pdf-header-type  { font-size: 16px; font-weight: 700; color: #0052cc; text-align: center; margin-top: 2px; letter-spacing: -0.2px; }
        }
        @media screen { .pdf-header { display: none; } }
      </style>
      <div class="pdf-header">
        <div class="pdf-header-top">
          <div class="pdf-header-title">${modelTitle || 'Assessment Report'}</div>
          <div class="pdf-header-meta">${dateStr} &nbsp;·&nbsp; ${timeStr}</div>
        </div>
        <div class="pdf-header-type">Assessment Report</div>
      </div>`;
    // If html is a fragment (savedHtml), wrap it in a full document first
    const fullHtml = html.includes('<body') ? html
      : `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;margin:24px;color:#172b4d;}</style></head><body>${html}</body></html>`;
    // Reports rendered with the current renderer carry their own masthead (cr-doc-header)
    // and print CSS — injecting the legacy pdf-header duplicates it and causes a
    // near-blank first page in Chromium.
    const printHtml = fullHtml.includes('cr-doc-header')
      ? fullHtml
      : fullHtml.replace(/<body[^>]*>/, (m) => m + printHeader);
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;width:0;height:0;border:none;left:-9999px;top:-9999px;';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(printHtml);
    iframe.contentDocument.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
    setIsExportModalOpen(false);
  };

  const handleExportJSON = () => {
    if (!report) return;
    const exportData = {
      metadata: { model: modelTitle, generatedAt: new Date().toISOString(), version: '1.0' },
      ...report,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `assessment-report-${(modelTitle || 'report').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setIsExportModalOpen(false);
  };
  const modelId = getModelID();

  const resolvedAiModelConfig = getAIModelConfig(aiModelConfig);
  const aiProvider = resolvedAiModelConfig.provider;
  const selectedModel = resolvedAiModelConfig.model;
  const temperature = resolvedAiModelConfig.temperature;
  const [requestId,           setRequestId]           = useState(null);
  const [status,              setStatus]              = useState(null);
  const [generationTime,      setGenerationTime]      = useState(null);
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [isGenerating,        setIsGenerating]        = useState(false);

  const pollIntervalRef = useRef(null);
  const aiProgressTimerRef = useRef(null);
  const compiledReportArtifactsRef = useRef(null);
  const reportScrollRef = useRef(null);

  const aiScope = useAiReportScopeControls({
    modelId,
    reportKey: 'consultingReport',
    reportLabel: 'Assessment Report',
    getTaskRows: taskJSON,
    getVariants: () => apiProvider.variantsTable() || [],
    getProcessSteps: () => apiProvider.processStepsTable() || [],
    getPrivacyRules: getAttributePrivacyRules, getActivityType, getActivityContext,
  });

  const updateGenerationStage = async (stageKey) => {
    const message = GENERATION_STAGES[stageKey] || stageKey || 'Generating Assessment Report';
    updateProgressModal({ status: message, ai: stageKey === 'writing' });
    await sleep(SLEEP_DELAY_MS);
  };

  // ── Initialise ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!modelTitle) setModelTitle(getProcessModelTitle());
    injectModalStyles();

    const cached = getCachedConsultantContent({ modelId, column: 'ai_consulting_report' });
    const dbHtml = getAiConsultantContent('ai_consulting_report');
    const saved = cached || (isHtmlCurrentVersion(dbHtml) ? dbHtml : null);
    if (saved) {
      setSavedHtml(saved);
      setReportGenerated(true);
    
      // Try session cache first, then extract embedded JSON from saved HTML
      const cachedJson = getCachedConsultantJson({ modelId, column: 'ai_consulting_report' });
      if (cachedJson) {
        setReport(cachedJson);
      } else {
        try {
          const match = saved.match(/<script id="__qa_data__"[^>]*>([\s\S]*?)<\/script>/);
          if (match) {
            const extracted = JSON.parse(match[1]);
            setReport(extracted);
            setCachedConsultantJson({ modelId, column: 'ai_consulting_report', json: extracted });
          }
        } catch (_) {}
      }
    }
  }, []);

  useEffect(() => {
    let alive = true;
    fetchOrganizationProfile().then(profile => { if (alive) setOrganizationProfile(profile || {}); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const orgFields = formatOrgFields(organizationProfile);

  const setReportOption = (key, value) => {
    setReportOptions(prev => ({ ...prev, [key]: value }));
  };

  // ── Forge polling ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!requestId || !(status?.status === 'pending' || status?.status === 'processing')) return;

    pollIntervalRef.current = setInterval(async () => {
      try {
        const { invoke } = await loadForgeModule();
        const statusData = await invoke('checkStatus', { requestId });
        setStatus(statusData);
        updateProgressModal({ status: `${GENERATION_STAGES.writing} (${processingCtr})`, ai: true });
        processingCtr += 1;

        if (statusData.status === 'completed') {
          let raw = statusData.result || '{}';
          // Strip markdown fences and any trailing content after the closing brace
                    let json = cleanAndParseJSON(raw);
          json = prepareReportForRendering(json, compiledReportArtifactsRef.current);
          replaceQuotes(json);
          const dur = ((Date.now() - generationStartTime) / 1000).toFixed(1);
          setGenerationTime(dur);
          console.log(`Assessment Report generated in ${dur}s`);
          setReport(json);
        setCachedConsultantJson({ modelId: getModelID(), column: 'ai_consulting_report', json });
          setReportGenerated(true);
          updateProgressModal({ status: GENERATION_STAGES.rendering, ai: false });
          const renderedHtml = renderConsultingReportHTML(json, getProcessModelTitle(), { theme: appDefaultReportTheme() });
          setSavedHtml(renderedHtml);
          setCachedConsultantContent({ modelId: getModelID(), column: 'ai_consulting_report', html: renderedHtml });
          updateProgressModal({ status: GENERATION_STAGES.saving, ai: false });
          saveConsultantContent({
            modelId:     getModelID(),
            column:      'ai_consulting_report',
            html:        renderedHtml,
            AppVersion: getAppVersion(),
          });
          setIsGenerating(false);
          closeProgressModal();
          clearInterval(pollIntervalRef.current);
        }
        if (statusData.status === 'failed') {
          setIsGenerating(false);
          closeProgressModal();
          clearInterval(pollIntervalRef.current);
        }
      } catch (err) {
        console.error('Error polling Assessment Report status:', err);
        setIsGenerating(false);
        closeProgressModal();
        clearInterval(pollIntervalRef.current);
      }
    }, 1000);

    return () => pollIntervalRef.current && clearInterval(pollIntervalRef.current);
  }, [requestId, status?.status]);

  // ── Data preparation ──────────────────────────────────────────────────────
  const prepareTransitions = () =>
    (aiScope.getScopedProcessSteps() || []).slice(0, 40).map(t => ({
      Transition:            t.step,
      'From Activity':       t.from_activity,
      'To Activity':         t.to_activity,
      'Work items':          t.tasks,
      'Avg deviation (hrs)': t.avg_duration_hrs?.toFixed(2)  ?? 'N/A',
      'Std deviation (hrs)': t.std_deviation_hrs?.toFixed(2) ?? 'N/A',
      [`% total ${apiProvider.getEntityUpper()}s`]:
        t.perc_total_tasks != null ? (t.perc_total_tasks * 100).toFixed(2) : 'N/A',
      'Is rework?': t.loop,
    }));

  const prepareVariants = () =>
    (aiScope.getScopedVariants() || []).slice(0, 30).map(v => ({
      Variant: v.variant,
      ID:      v.variant_id,
      [`${apiProvider.getEntityUpper()}s`]: v.tasks,
      [`% Total ${apiProvider.getEntityUpper()}s`]:
        v.perc_total_tasks != null ? (Number(v.perc_total_tasks) * 100).toFixed(2) : 'N/A',
      'Is Rework':           v.loop,
      Loops:                 v.loops,
      'Std Deviation (hrs)': v.std_deviation_hrs != null ? Number(v.std_deviation_hrs).toFixed(2) : 'N/A',
      'Avg Duration (hrs)':  v.avg_duration_hrs  != null ? Number(v.avg_duration_hrs).toFixed(2)  : 'N/A',
    }));

  const prepareMetrics = () =>
    getModelMetrics()
      .filter(m => m.work_items > 0)
      .map(m => ({
        [`${apiProvider.getEntityUpper()}s`]: m.work_items,
        Variants:          m.variants,
        Activities:        m.activities,
        Steps:             m.steps,
        'Avg Transitions': m.avg_transitions.toFixed(1),
        'Avg Duration':    (m.cycle_time    / 3600).toFixed(2),
        'Std Deviation':   (m.std_deviation / 3600).toFixed(2),
        '% Rework':        (m.rework_rate   * 100 ).toFixed(2),
      }));

  const currentWorkbench = typeof getConsultingWorkspace === 'function' ? getConsultingWorkspace() : null;
  const currentAdvisorContent = buildAdvisorContentPackage(modelId);
  const currentMetricsHistory = prepareMetrics();
  const currentTransitions = prepareTransitions();
  const currentVariants = prepareVariants();
  const currentAvailability = buildAvailabilityMap({
    reportScopeSummary: aiScope.getReportScopeSummary(),
    organizationProfile,
    orgFields,
    metricsHistory: currentMetricsHistory,
    transitions: currentTransitions,
    variants: currentVariants,
    workbench: currentWorkbench,
    advisorContent: currentAdvisorContent,
  });
  const reportSectionRows = buildSectionRows({ reportOptions, availability: currentAvailability });
  const selectedAvailableCount = reportSectionRows.filter(row => row.selected).length;
  const missingRecommended = reportSectionRows.filter(row => !row.optional && row.source.status === 'missing');
  const canGenerateReport = selectedAvailableCount > 0 && currentAvailability.scope.status !== 'missing' && currentAvailability.evidence.status !== 'missing' && currentAvailability.advisor.status !== 'missing';
  const selectedPreviewSection = reportSectionRows.find(row => row.optionKey === previewSectionKey) || reportSectionRows[0];
  const selectedPreview = buildSectionPreview({
    row: selectedPreviewSection,
    reportSectionRows,
    modelTitle,
    reportScopeSummary: aiScope.getReportScopeSummary(),
    orgFields,
    metricsHistory: currentMetricsHistory,
    transitions: currentTransitions,
    variants: currentVariants,
    advisorContent: currentAdvisorContent,
    workbench: currentWorkbench,
  });

  useEffect(() => {
    if (!reportGenerated) return;
    setTimeout(() => {
      try {
        reportScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
        if (typeof window !== 'undefined') window.scrollTo?.({ top: 0, behavior: 'smooth' });
      } catch (_) {}
    }, 100);
  }, [reportGenerated, savedHtml]);

  const navigateToAssessmentTab = (tabId) => {
    if (!tabId || typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('metricus:consultant-tab-change', { detail: { tabId } }));
  };

  // ── Generate ──────────────────────────────────────────────────────────────
  const handleSaveSnapshot = async (description) => {
    setSnapshotSaving(true);
    try {
      const html = savedHtml || null;
      if (!html) return;
      const result = await saveSnapshot({
        modelId, tab: 'consultingReport', tabLabel: 'Assessment Report',
        description, html, AppVersion: getAppVersion(),
      });
      if (!result?.success) throw new Error('Snapshot was not saved');
      setSnapshotModalOpen(false);
    } catch (error) {
      console.error('Advisor snapshot save failed', error);
      window.alert(`Unable to save Advisor Snapshot: ${error.message || error}`);
    } finally {
      setSnapshotSaving(false);
    }
  };

  const generateReport = async () => {
    stopAllAiProgressTimers();
    const startTime = Date.now();
    setGenerationStartTime(startTime);
    setGenerationTime(null);
    setIsGenerating(true);

    await openProgressModal({ status: GENERATION_STAGES.checking });
    await sleep(SLEEP_DELAY_MS);

    const API_KEY      = undefined;

    const issueTypes   = getIssueTypes();
    const issueTypeStr = issueTypes.length === 1
      ? `Work Type = ${issueTypes[0]}`
      : `Work Types = ${issueTypes.join(' or ')}`;

    await updateGenerationStage('scope');
    const fieldUsage = getFieldUsage();
    const statusTypeSummary = getStatusTypeSummary ? getStatusTypeSummary() : null;
    const statusTypeMappings = getStatusTypeMappings ? getStatusTypeMappings() : [];
    const activityType = typeof getActivityType === 'function' ? getActivityType() : null;
    const activityContext = typeof getActivityContext === 'function' ? getActivityContext() : null;
    const reportScopeSummary = aiScope.getReportScopeSummary();
    const workbench = typeof getConsultingWorkspace === 'function' ? getConsultingWorkspace() : null;

    await updateGenerationStage('snapshot');
    await updateGenerationStage('map');
    await updateGenerationStage('evidence');
    const scopedRows = aiScope.getScopedTaskRows();
    const _crPrivacy = applySampleItemPrivacy(scopedRows, getAttributePrivacyRules());
    const _crMetricsTotal = typeof getModelMetricsTotal === 'function' ? getModelMetricsTotal() : null;
    const metricsHistory = prepareMetrics();
    const transitionsJSON = prepareTransitions();
    const variantsJSON = prepareVariants();

    await updateGenerationStage('segments');
    await updateGenerationStage('advisor');
    const advisorContent = buildAdvisorContentPackage(modelId);

    await updateGenerationStage('curated');
    const availability = buildAvailabilityMap({
      reportScopeSummary,
      organizationProfile,
      orgFields,
      metricsHistory,
      transitions: transitionsJSON,
      variants: variantsJSON,
      workbench,
      advisorContent,
    });
    await updateGenerationStage('value');
    const selectedAvailableOptions = REPORT_SECTION_DEFS.reduce((acc, def) => {
      const source = availability?.[def.sourceKey];
      const selectable = source && source.status !== 'missing';
      acc[def.optionKey] = !!reportOptions[def.optionKey] && selectable;
      return acc;
    }, {});
    const compiledEvidencePack = buildEvidencePack({
      modelTitle: getProcessModelTitle(),
      issueTypeStr,
      reportScopeSummary,
      metricsTotal: _crMetricsTotal,
      metricsHistory,
      transitionsJSON,
      variantsJSON,
      fieldUsage,
      statusTypeSummary,
      statusTypeMappings,
      activityType,
      activityContext,
      workbench,
      advisorContent,
      selectedOptions: selectedAvailableOptions,
      organizationProfile,
    });
    compiledReportArtifactsRef.current = compiledEvidencePack;
    await updateGenerationStage('appendices');

    const assessmentReportInputs = buildAssessmentReportInputs({
      reportScopeSummary,
      workbench,
      organizationProfile,
      reportOptions: selectedAvailableOptions,
      advisorContent,
      availability,
    });
    assessmentReportInputs.compiled_evidence_pack = compiledEvidencePack;

    await updateGenerationStage('package');
    const inputEvidence = {
      ...buildSharedConsultantPayload({
      issueTypeStr,
      metricsHistory,
      metricsTotal: _crMetricsTotal,
      transitionsJSON,
      variantsJSON,
      sampleItems:     _crPrivacy.rows,
      fieldUsage,
      statusTypeSummary,
      statusTypeMappings,
      activityType,
      activityContext,
      appVersion: getAppVersion(),
      sourceSystem: getAppVersion() === 'SNOW' ? 'servicenow' : 'jira',
    }),
      assessment_report_inputs: assessmentReportInputs,
      compiled_evidence_pack: compiledEvidencePack,
    };

    const isForgeApp = getAppVersion() === 'FORGE';
    const promptArgs = isForgeApp
      ? buildForgeAssessmentPromptArgs({
          inputEvidence,
          compiledEvidencePack,
          assessmentReportInputs,
          issueTypeStr,
          metricsHistory,
          metricsTotal: _crMetricsTotal,
          transitionsJSON,
          variantsJSON,
          fieldUsage,
          statusTypeSummary,
          statusTypeMappings,
          activityType,
          activityContext,
          appVersion: getAppVersion(),
          sourceSystem: getAppVersion()==='FORGE'
            ? 'jira'
            : getAppVersion()==='SNOW'
              ? 'servicenow'
              : (typeof window!=='undefined' ? (window.__PA_WEB_AI_CONTEXT__?.model?.sourceSystem || 'generic') : 'generic'),
          reportScopeSummary,
        })
      : {
          inputPayload: inputEvidence,
          issueTypeStr,
          metricsHistory,
          metricsTotal: _crMetricsTotal,
          transitionsJSON,
          variantsJSON,
          sampleItems: _crPrivacy.rows,
          fieldUsage,
          statusTypeSummary,
          statusTypeMappings,
          activityType,
          activityContext,
          appVersion: getAppVersion(),
          sourceSystem: getAppVersion()==='SNOW'
            ? 'servicenow'
            : getAppVersion()==='FORGE'
              ? 'jira'
              : (typeof window!=='undefined' ? (window.__PA_WEB_AI_CONTEXT__?.model?.sourceSystem || 'generic') : 'generic'),
          reportScopeSummary,
        };

    let prompt = buildConsultingReportPrompt(promptArgs);
    prompt = await injectOrganizationContext(prompt);

    if (isForgeApp && prompt.length > FORGE_ASSESSMENT_REPORT_PROMPT_TARGET) {
      console.warn(`Assessment Report Forge prompt is ${prompt.length} characters after compaction. Applying final safe trim to avoid Forge invoke payload limit.`);
      prompt = truncateText(prompt, FORGE_ASSESSMENT_REPORT_PROMPT_TARGET);
    }

    if (isForgeApp) {
      const beforeBytes = estimateUtf8Bytes({
        prompt,
        aiProvider: toInvokeProvider(aiProvider),
        model: selectedModel,
        temperature,
      });
      const fittedPrompt = fitForgeAssessmentPromptForInvoke(prompt, {
        aiProvider: toInvokeProvider(aiProvider),
        model: selectedModel,
        temperature,
      });
      if (fittedPrompt !== prompt) {
        const afterBytes = estimateUtf8Bytes({
          prompt: fittedPrompt,
          aiProvider: toInvokeProvider(aiProvider),
          model: selectedModel,
          temperature,
        });
        console.warn(`Assessment Report Forge invoke payload reduced before queueing: ${beforeBytes} bytes -> ${afterBytes} bytes.`);
        prompt = fittedPrompt;
      }
    }

    // ── Forge path ──
    if (isForgeApp) {
      processingCtr = 1;
      try {
        const { invoke } = await loadForgeModule();
        const response = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'Assessment Report',
          payload: {
            prompt,
            aiProvider: toInvokeProvider(aiProvider),
            model: selectedModel,
            temperature,
          },
        });
        setRequestId(response.requestId);
        updateProgressModal({ status: `${GENERATION_STAGES.writing} - request queued`, ai: true });
        setStatus({ status: 'pending' });
      } catch (error) {
        console.error('Forge error (ConsultingReport):', error);
        setIsGenerating(false);
        closeProgressModal();
      }
      return;
    }

    // ── Web SaaS path ──
    if (getAppVersion() === 'WEB') {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      await updateGenerationStage('writing');
      aiProgressTimerRef.current = await startAiProgressTimer(GENERATION_STAGES.writing);
      try {
        const result = await invokeWebAI({
          prompt,
          reportLabel:'Assessment Report',
          temperature,
          responseFormat:'json',
          modelId:getModelID(),
        });
        let json = cleanAndParseJSON(result?.text || '{}');
        json = prepareReportForRendering(json, compiledEvidencePack);
        replaceQuotes(json);
        const dur=((Date.now()-startTime)/1000).toFixed(1);
        setGenerationTime(dur);
        setReport(json);
        setCachedConsultantJson({modelId:getModelID(),column:'ai_consulting_report',json});
        setReportGenerated(true);
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current=null;
        await updateGenerationStage('rendering');
        const renderedHtml=renderConsultingReportHTML(json,getProcessModelTitle(),{theme:appDefaultReportTheme()});
        setSavedHtml(renderedHtml);
        setCachedConsultantContent({modelId:getModelID(),column:'ai_consulting_report',html:renderedHtml});
        await updateGenerationStage('saving');
        saveConsultantContent({
          modelId:getModelID(),
          column:'ai_consulting_report',
          html:renderedHtml,
          AppVersion:getAppVersion(),
        });
      } catch (error) {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current=null;
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current=null; }
        console.error('[ConsultingReport] Web AI error:',error);
      }
      stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current=null;
      if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current=null; }
      setIsGenerating(false);closeProgressModal();
      return;
    }

    // ── Cloud direct path ──
    if (getAppVersion() === 'SNOW') {
      const _geminiKey = (typeof API_KEY !== 'undefined' && API_KEY) ? API_KEY : undefined;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      await updateGenerationStage('writing');
      aiProgressTimerRef.current = await startAiProgressTimer(GENERATION_STAGES.writing);
      try {
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${selectedModel}:generateContent?key=${_geminiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: prompt + '\n\nYou must output valid JSON. Do not include ``` at beginning and end' }] }],
              generationConfig: { responseMimeType: 'application/json', maxOutputTokens: 32768 },
            }),
          }
        );
        const responseData = await response.json();
        let text = responseData?.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
        let json = cleanAndParseJSON(text);
        json = prepareReportForRendering(json, compiledEvidencePack);
        replaceQuotes(json);
        const dur = ((Date.now() - startTime) / 1000).toFixed(1);
        setGenerationTime(dur);
        console.log(`Assessment Report generated in ${dur}s`);
        setReport(json);
        setCachedConsultantJson({ modelId: getModelID(), column: 'ai_consulting_report', json });
        setReportGenerated(true);
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        await updateGenerationStage('rendering');
        const renderedHtml = renderConsultingReportHTML(json, getProcessModelTitle(), { theme: appDefaultReportTheme() });
        setSavedHtml(renderedHtml);
        setCachedConsultantContent({ modelId: getModelID(), column: 'ai_consulting_report', html: renderedHtml });
        await updateGenerationStage('saving');
        saveConsultantContent({
          modelId:     getModelID(),
          column:      'ai_consulting_report',
          html:        renderedHtml,
          AppVersion: getAppVersion(),
        });
      } catch (error) {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
        console.error('direct API error (ConsultingReport):', error);
      }
    }

    stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
    clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
    setIsGenerating(false);
    closeProgressModal();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ backgroundColor: '#f8f9fa', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div ref={reportScrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

      {/* ── Header bar ── */}
        <ConsultantTabHeader
        title="Assessment Report"
        subtitle="Final client-ready assessment report — scope, evidence, Advisor insights, curated findings, value analysis, recommendations, and next steps"
        icon={<FileText size={20} color="white" />}
        iconBackground="linear-gradient(135deg, #0052CC 0%, #2684FF 100%)"
        titleColor="#0052CC"
        actions={(
          <>
            {!reportGenerated && <ReadyBadge color="#0C66E4" />}
            {reportGenerated && (
              <>
                <AskAiButton hasContent={reportGenerated} onClick={() => setQaOpen(true)} />
                <button
                  style={getHeaderPrimaryButtonStyle('#0052CC')}
                  onClick={() => { setSavedHtml(null); setReportGenerated(false); }}
                >
                  Regenerate
                </button>
                <button
                  style={getHeaderSecondaryButtonStyle('#0052CC')}
                  onClick={() => setIsExportModalOpen(true)}
                >
                  Export Report
                </button>
                {(typeof window === "undefined" || window.__METRICUS_JIRA_PRODUCT_EDITION__ !== "free") && <SaveSnapshotButton color="#0052CC" onClick={() => setSnapshotModalOpen(true)} />}
              </>
            )}
          </>
        )}
      />
        {/* ── Report builder panel ── */}
      {!reportGenerated && (
        <div style={{ background: '#F7F8FA', padding: '16px', margin: '0 10px 10px', border: '1px solid #DFE1E6', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
          <div style={{ background: '#FFFFFF', border: '1px solid #DFE1E6', borderRadius: 10, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#172B4D' }}>Report readiness and sections</div>
                <div style={{ fontSize: 12, color: '#5E6C84', marginTop: 2, lineHeight: 1.45 }}>
                  The report uses all available assessment context. Select which sections should appear in the final output.
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#5E6C84', whiteSpace: 'nowrap' }}>{selectedAvailableCount} selected</div>
            </div>

            {missingRecommended.length > 0 && (
              <div style={{ border: '1px solid #FFE380', background: '#FFF7D6', color: '#7A4A00', borderRadius: 8, padding: '9px 10px', fontSize: 12, marginBottom: 12, lineHeight: 1.45 }}>
                <strong>{missingRecommended.length} recommended section{missingRecommended.length === 1 ? '' : 's'} missing.</strong> Scope, evidence, and Process Health are required. Findings, actions, and value analysis are optional enhancements.
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 10 }}>
              {reportSectionRows.map((row) => {
                const meta = getStatusMeta(row.source.status);
                const Icon = meta.icon;
                const canNavigateAdvisor = row.sourceKey === 'advisor';
                const canNavigateFindings = row.sourceKey === 'findings';
                const canNavigateActions = row.sourceKey === 'actions';
                return (
                  <div
                    key={row.optionKey}
                    style={{
                      ...reportCheckboxStyle,
                      alignItems: 'flex-start',
                      opacity: row.selectable ? 1 : 0.78,
                      background: row.selectable ? '#FFFFFF' : '#FAFBFC',
                      borderColor: row.selectable ? '#DFE1E6' : '#EBECF0',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={row.selected}
                      disabled={!row.selectable}
                      onChange={(e) => setReportOption(row.optionKey, e.target.checked)}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#172B4D' }}>{row.label}</div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 999, background: meta.bg, color: meta.color, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>
                          <Icon size={12} /> {meta.label}{row.source.count ? ` · ${row.source.count}` : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#5E6C84', lineHeight: 1.45, marginTop: 3 }}>{row.description}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 5 }}>
                        <div style={{ fontSize: 11, color: meta.color, fontWeight: 700 }}>{row.source.message}</div>
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {canNavigateAdvisor && row.source.status === 'missing' && (
                            <button type="button" onClick={() => navigateToAssessmentTab('processHealth')} style={miniLinkButtonStyle}>Go to Process Health <ExternalLink size={11} /></button>
                          )}
                          {canNavigateFindings && row.source.status === 'missing' && (
                            <button type="button" onClick={() => navigateToAssessmentTab('workbenchFindings')} style={miniLinkButtonStyle}>Add findings <ExternalLink size={11} /></button>
                          )}
                          {canNavigateActions && row.source.status === 'missing' && (
                            <button type="button" onClick={() => navigateToAssessmentTab('workbenchActions')} style={miniLinkButtonStyle}>Add actions <ExternalLink size={11} /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                onClick={generateReport}
                disabled={isGenerating || !canGenerateReport}
                style={{
                  background: (isGenerating || !canGenerateReport) ? '#dfe1e6' : '#0C66E4',
                  color: (isGenerating || !canGenerateReport) ? '#97a0af' : '#fff',
                  border: 'none', padding: '10px 22px', borderRadius: 6,
                  fontSize: 14, fontWeight: 700,
                  cursor: (isGenerating || !canGenerateReport) ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  boxShadow: isGenerating ? 'none' : '0 2px 6px rgba(12,102,228,0.25)',
                }}
              >
                <FileText size={18} />
                {isGenerating ? 'Generating...' : (!canGenerateReport ? 'Complete required sections' : 'Generate Comprehensive Assessment Report')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rendered output ── */}
      {reportGenerated && savedHtml && (
        <HtmlPreviewFrame html={savedHtml} title="Assessment Report Preview" minHeight={1600} />
      )}
      {reportGenerated && !savedHtml && report && (
        <HtmlPreviewFrame html={renderConsultingReportHTML(report, modelTitle, { theme: appDefaultReportTheme() })} title="Assessment Report Preview" minHeight={1600} />
      )}

      </div>{/* end scroll-content */}
      <ConsultantQaPanel
        isOpen={qaOpen}
        onClose={() => setQaOpen(false)}
        tabId="report"
        tabLabel="Assessment Report"
        themeColor="#0747A6"
        resultData={report}
        savedHtml={savedHtml}
        generated={reportGenerated}
        modelTitle={modelTitle}
        processLabel={processLabel}
        entityLabel={entityLabel}
        aiModelConfig={resolvedAiModelConfig}
      />

      <SnapshotListPanel
        isOpen={snapshotPanelOpen}
        onClose={() => setSnapshotPanelOpen(false)}
        modelId={modelId}
        AppVersion={getAppVersion()}
      />

      <SaveSnapshotModal
        isOpen={snapshotModalOpen}
        onClose={() => setSnapshotModalOpen(false)}
        onSave={handleSaveSnapshot}
        tab="consultingReport"
        tabLabel="Assessment Report"
        saving={snapshotSaving}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export Assessment Report"
        onExportHTML={handleExportHTML}
        onExportPDF={handleExportPDF}
        onExportPPTX={report ? handleExportPPTX : undefined}
        onExportJSON={handleExportJSON}
      />
    </div>
  );
};

export default ConsultingReport;
